// src/components/IOCView.js - OPTIMIZED VERSION WITH VIRUSTOTAL
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import UrlhausSyncPanel from './UrlhausSyncPanel';
import PhishingIOCSyncPanel from './PhishingIOCSyncPanel';
import VirusTotalPanel from './VirusTotalPanel';
import AbuseIPDBEnrichment from './AbuseIPDBEnrichment';
import OpenPhishSyncPanel from './OpenPhishSyncPanel';


import { 
  Shield, Search, Filter, Download, Upload, RefreshCw,
  AlertTriangle, Globe, FileText, Hash, Mail, Key,
  Calendar, TrendingUp, ExternalLink, Plus, Edit2, Trash2,
  Database, Activity, ChevronDown, ChevronUp, Server
} from 'lucide-react';

// IOC Type Icons Mapping
const IOC_TYPE_ICONS = {
  'IP Address': Globe,
  'Domain': Globe,
  'URL': ExternalLink,
  'File Hash (MD5)': Hash,
  'File Hash (SHA-1)': Hash,
  'File Hash (SHA-256)': Hash,
  'Email Address': Mail,
  'Mutex': Key,
  'Registry Key': FileText,
  'CVE': AlertTriangle
};

// Risk Score Color Coding
const getRiskColor = (score) => {
  if (score >= 80) return 'text-red-400 bg-red-500/10';
  if (score >= 60) return 'text-orange-400 bg-orange-500/10';
  if (score >= 40) return 'text-yellow-400 bg-yellow-500/10';
  return 'text-green-400 bg-green-500/10';
};

const getRiskLabel = (score) => {
  if (score >= 80) return 'Critical';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Medium';
  return 'Low';
};

const IOCView = ({ isSuperAdmin, canEditIncidents }) => {
  const { getToken } = useAuth();
  const { activeCompany } = useTenant();

  // State Management
  const [iocs, setIocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedIoc, setSelectedIoc] = useState(null);
  
  // Stats state (lightweight - just counts)
  const [stats, setStats] = useState({
    total: 0,
    by_type: {},
    by_source: {},
    recent_24h: 0
  });

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterThreatType, setFilterThreatType] = useState('all');
  const [sortBy, setSortBy] = useState('last_seen');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load ONLY stats on initial mount - NO IOC data
  useEffect(() => {
    if (!initialLoad) {
      fetchStats();
      setInitialLoad(true);
    }
  }, [initialLoad]);

  // Fetch lightweight stats only
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/iocs/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching IOC stats:', error);
    }
  };

  // Fetch IOCs - only called when user searches or clicks load button
  const fetchIocs = async (searchQuery = '') => {
    try {
      setLoading(true);
      const limit = searchQuery ? 500 : 100;
      const url = `/api/iocs?limit=${limit}&search=${encodeURIComponent(searchQuery)}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setIocs(data);
      } else {
        console.error('Failed to fetch IOCs:', response.status);
      }
    } catch (error) {
      console.error('Error fetching IOCs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      fetchIocs(searchTerm.trim());
    }
  };

  const handleLoadRecent = () => {
    setSearchTerm('');
    fetchIocs('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Apply client-side filters
  const getFilteredIocs = () => {
    let filtered = [...iocs];

    if (filterType !== 'all') {
      filtered = filtered.filter(ioc => 
        ioc.ioc_type?.toLowerCase().includes(filterType.toLowerCase())
      );
    }
    if (filterSource !== 'all') {
      filtered = filtered.filter(ioc => 
        ioc.source?.toLowerCase().includes(filterSource.toLowerCase())
      );
    }
    if (filterThreatType !== 'all') {
      filtered = filtered.filter(ioc => 
        ioc.threat_type?.toLowerCase().includes(filterThreatType.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'first_seen' || sortBy === 'last_seen') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

    return filtered;
  };

  const filteredIocs = getFilteredIocs();
  const uniqueThreatTypes = [...new Set(iocs.map(ioc => ioc.threat_type).filter(Boolean))];

  // Build stat cards from stats data
  const domainCount = stats.by_type?.['Domain'] || 0;
  const ipCount = stats.by_type?.['IP Address'] || 0;
  const hashCount = (stats.by_type?.['File Hash (MD5)'] || 0) + 
                    (stats.by_type?.['File Hash (SHA-1)'] || 0) + 
                    (stats.by_type?.['File Hash (SHA-256)'] || 0);
  const urlCount = stats.by_type?.['URL'] || 0;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[1600px] mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" />
            Indicators of Compromise (IOCs)
          </h1>
          <p className="mt-2 text-gray-400">
            Search and analyze threat indicators from multiple intelligence sources
          </p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Total IOCs</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.total.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">in database</div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Domains</span>
            </div>
            <div className="text-2xl font-bold text-white">{domainCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">tracked</div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">IP Addresses</span>
            </div>
            <div className="text-2xl font-bold text-white">{ipCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">tracked</div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">File Hashes</span>
            </div>
            <div className="text-2xl font-bold text-white">{hashCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">tracked</div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ExternalLink className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">URLs</span>
            </div>
            <div className="text-2xl font-bold text-white">{urlCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">tracked</div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-yellow-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Last 24h</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.recent_24h.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">new</div>
          </div>
        </div>

        {/* Sync Panels */}
        {isSuperAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
  <UrlhausSyncPanel onSyncComplete={fetchStats} />
  <PhishingIOCSyncPanel onSyncComplete={fetchStats} />
  <OpenPhishSyncPanel onSyncComplete={fetchStats} />
</div>
        )}

        {/* Search & Filters */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            {/* Search Bar */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-400 mb-2">Search IOCs</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by IOC value, hash, domain, IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="w-full lg:w-48">
              <label className="block text-sm font-medium text-gray-400 mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="ip">IP Address</option>
                <option value="domain">Domain</option>
                <option value="url">URL</option>
                <option value="hash">File Hash</option>
              </select>
            </div>

            {/* Threat Type Filter */}
            {uniqueThreatTypes.length > 0 && (
              <div className="w-full lg:w-48">
                <label className="block text-sm font-medium text-gray-400 mb-2">Threat Type</label>
                <select
                  value={filterThreatType}
                  onChange={(e) => setFilterThreatType(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Threats</option>
                  {uniqueThreatTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                disabled={!searchTerm.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Search
              </button>
              <button
                onClick={handleLoadRecent}
                className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Load Recent 100
              </button>
            </div>
          </div>

          {/* Quick Filters Info */}
          {(filterType !== 'all' || filterThreatType !== 'all') && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <Filter className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400">Active filters:</span>
              {filterType !== 'all' && (
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                  Type: {filterType}
                </span>
              )}
              {filterThreatType !== 'all' && (
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                  Threat: {filterThreatType}
                </span>
              )}
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterThreatType('all');
                }}
                className="ml-2 text-red-400 hover:text-red-300"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12">
            <div className="flex flex-col items-center justify-center">
              <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Loading IOCs...</h3>
              <p className="text-gray-400">Searching threat intelligence database</p>
            </div>
          </div>
        )}

        {/* Empty State - No Search Yet */}
        {!loading && iocs.length === 0 && !searchTerm && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
                <Search className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">Ready to Search</h3>
              <p className="text-gray-400">
                Enter a search term above to find specific indicators, or click "Load Recent 100" to view the latest threat intelligence.
              </p>
            </div>
          </div>
        )}

        {/* No Results After Search */}
        {!loading && iocs.length === 0 && searchTerm && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Results Found</h3>
              <p className="text-gray-400">
                No IOCs match your search term "<span className="text-blue-400">{searchTerm}</span>"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Clear Search
              </button>
            </div>
          </div>
        )}

        {/* Results Table */}
        {!loading && iocs.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr className="border-b border-slate-700">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IOC Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Threat Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Risk</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredIocs.map((ioc) => {
                    const Icon = IOC_TYPE_ICONS[ioc.ioc_type] || Shield;
                    return (
                      <tr
                        key={ioc.ioc_id}
                        className="hover:bg-slate-700/30 cursor-pointer transition-colors"
                        onClick={() => setSelectedIoc(ioc)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <span className="text-white font-mono text-sm truncate max-w-xs">{ioc.ioc_value}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-300 text-sm">{ioc.ioc_type}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                            {ioc.threat_type || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {ioc.risk_score != null ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(ioc.risk_score)}`}>
                              {getRiskLabel(ioc.risk_score)} ({ioc.risk_score})
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-400 text-sm truncate max-w-xs block" title={ioc.source}>
                            {ioc.source?.replace('AlienVault OTX - ', '') || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-400 text-sm">
                            {ioc.last_seen ? new Date(ioc.last_seen).toLocaleDateString() : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredIocs.length === 0 && (
              <div className="p-12 text-center">
                <Filter className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No IOCs match the selected filters</p>
              </div>
            )}
          </div>
        )}

        {/* IOC Detail Modal */}
        {selectedIoc && createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex justify-between items-center z-10">
                <h3 className="text-xl font-bold text-white">IOC Details</h3>
                <button onClick={() => setSelectedIoc(null)} className="text-gray-400 hover:text-white">
                  <span className="text-2xl">×</span>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Details */}
                <div>
                  <label className="text-sm font-medium text-gray-400">IOC Value</label>
                  <p className="mt-1 text-white font-mono bg-slate-900 p-3 rounded border border-slate-700 break-all">
                    {selectedIoc.ioc_value}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-400">Type</label>
                    <p className="mt-1 text-white">{selectedIoc.ioc_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-400">Risk Score</label>
                    {selectedIoc.risk_score != null ? (
                      <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(selectedIoc.risk_score)}`}>
                        {getRiskLabel(selectedIoc.risk_score)} ({selectedIoc.risk_score})
                      </span>
                    ) : (
                      <p className="mt-1 text-gray-500">N/A</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400">Source</label>
                  <p className="mt-1 text-white">{selectedIoc.source}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400">Threat Type</label>
                  <p className="mt-1 text-white">{selectedIoc.threat_type || 'N/A'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-400">First Seen</label>
                    <p className="mt-1 text-white">
                      {selectedIoc.first_seen ? new Date(selectedIoc.first_seen).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-400">Last Seen</label>
                    <p className="mt-1 text-white">
                      {selectedIoc.last_seen ? new Date(selectedIoc.last_seen).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>

                {selectedIoc.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Description</label>
                    <p className="mt-1 text-gray-300 bg-slate-900 p-3 rounded border border-slate-700">
                      {selectedIoc.description}
                    </p>
                  </div>
                )}

                {selectedIoc.tags && selectedIoc.tags.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Tags</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedIoc.tags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/30">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Threat Intelligence Enrichment */}
                <div>
                  <label className="text-sm font-medium text-gray-400 mb-3 block">Threat Intelligence Enrichment</label>
                  <div className="space-y-4">
                    <VirusTotalPanel ioc={selectedIoc} />
                    {selectedIoc.ioc_type === 'IP Address' && (
                      <AbuseIPDBEnrichment ipAddress={selectedIoc.ioc_value} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
        </div>
      </div>
    </div>
  );
};

export default IOCView;